import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  X 
} from "lucide-react";
import { useGetLndlordOutboxMessagesQuery } from "../../lib/apiSlice/communication/communicationSlice.tsx";
import { 
  format, 
  parseISO, 
  startOfToday, 
  endOfToday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear
} from 'date-fns';

// Skeleton Loader Component
const TableSkeleton = () => {
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <table className="divide-y divide-gray-200 min-w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
              Recipient
            </th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
              Subject
            </th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
              Content Preview
            </th>
            <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
              Sent At
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[...Array(5)].map((_, i) => (
            <tr key={i}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded w-3/4 h-4 animate-pulse"></div>
              </td>
              <td className="px-6 py-4">
                <div className="bg-gray-200 rounded w-1/2 h-4 animate-pulse"></div>
              </td>
              <td className="px-6 py-4">
                <div className="space-y-2">
                  <div className="bg-gray-200 rounded w-full h-4 animate-pulse"></div>
                  <div className="bg-gray-200 rounded w-5/6 h-4 animate-pulse"></div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="bg-gray-200 rounded w-2/3 h-4 animate-pulse"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function MessageHistory() {
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [datePreset, setDatePreset] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryArgs = useMemo(() => ({
    page: currentPage,
    pageSize: itemsPerPage,
    search: debouncedSearch,
    startDate: dateRange.start ? format(dateRange.start, 'yyyy-MM-dd') : undefined,
    endDate: dateRange.end ? format(dateRange.end, 'yyyy-MM-dd') : undefined,
  }), [currentPage, debouncedSearch, dateRange.start, dateRange.end]);

  const {
    data: outboxMessages,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetLndlordOutboxMessagesQuery(queryArgs);

  // Date presets handler
  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    
    switch(preset) {
      case "today":
        setDateRange({
          start: startOfToday(),
          end: endOfToday()
        });
        break;
      case "week":
        setDateRange({
          start: startOfWeek(today),
          end: endOfWeek(today)
        });
        break;
      case "month":
        setDateRange({
          start: startOfMonth(today),
          end: endOfMonth(today)
        });
        break;
      case "lastMonth":
        setDateRange({
          start: startOfMonth(subMonths(today, 1)),
          end: endOfMonth(subMonths(today, 1))
        });
        break;
      case "year":
        setDateRange({
          start: startOfYear(today),
          end: endOfYear(today)
        });
        break;
      default:
        setDateRange({
          start: null,
          end: null
        });
    }
    setCurrentPage(1);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setDatePreset("all");
    setDateRange({
      start: null,
      end: null
    });
    setCurrentPage(1);
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const totalMessages = outboxMessages?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalMessages / itemsPerPage));
  const paginatedMessages = outboxMessages?.results ?? [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="font-semibold text-gray-800 text-2xl">Message History</h1>
        {(searchTerm || datePreset !== "all") && (
          <button 
            onClick={resetFilters}
            className="flex items-center gap-2 text-blue-500 hover:text-blue-700 text-sm"
          >
            <X className="w-4 h-4" />
            Reset Filters
          </button>
        )}
      </div>

      <div className="bg-white shadow-sm p-6 rounded-lg">
        <div className="flex sm:flex-row flex-col gap-4 mb-6">
          <div className="relative flex-1 px-2">
            <Search className="top-2.5 left-5 absolute w-4 h-4 text-gray-400" />
            <input
              placeholder="Search messages..."
              className="py-2 ps-5 pl-1 border border-gray-300 focus:border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-50"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              className="hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700"
              value={datePreset}
              onChange={(e) => handleDatePreset(e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {(isLoading || isFetching) && <TableSkeleton />}

        {error && !(isLoading || isFetching) && (
          <div className="bg-red-50 p-6 border border-red-200 rounded-md text-center">
            <p className="mb-3 text-red-700">Messages could not be loaded.</p>
            <button
              type="button"
              onClick={refetch}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-md text-white text-sm"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loaded State */}
        {!error && !(isLoading || isFetching) && (
          <>
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <table className="divide-y divide-gray-200 min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                      Content Preview
                    </th>
                    <th className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                      Sent At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedMessages.length > 0 ? (
                    paginatedMessages.map((message) => (
                      <tr key={message.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {message.recipient}
                          </div>
                        </td>
                        <td className="px-6 py-4 max-w-[200px] text-sm truncate">
                          {message.subject || "No subject"}
                        </td>
                        <td className="px-6 py-4 max-w-[300px] text-sm">
                          <div className="line-clamp-2">
                            {message.content}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                          {formatDate(message.created_at)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-gray-500 text-sm text-center">
                        No messages found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalMessages > itemsPerPage && (
              <div className="flex justify-between items-center mt-4">
                <div className="text-gray-500 text-sm">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalMessages)} of{" "}
                  {totalMessages} messages
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="disabled:opacity-50 p-1 border border-gray-300 rounded-md"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="bg-gray-100 px-3 py-1 rounded-md text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="disabled:opacity-50 p-1 border border-gray-300 rounded-md"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
